import React, { createContext, useContext, useReducer, useRef } from 'react';

import type { Dispatch } from 'react';

export type AudioStep = 'ready' | 'recording' | 'pause' | 'waiting' | '';

export type StepFlowType =
  | 'basic'
  | 'pauseable'
  | 'oneClick'
  | 'recordOnly'
  | 'instantRecord';

interface RecorderState {
  step: AudioStep;
  analyser: AnalyserNode | null;
  isRecording: boolean;
  file: File | null;
  audioUrl: string;
}

type RecorderContextType = {
  state: RecorderState;
  dispatch: Dispatch<RecorderAction>;
  startRecording: (onComplete?: (file: File) => void) => Promise<void>;
  stopRecording: (isSubmit: boolean) => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  setIsRecording: (isRecording: boolean) => void;
  setStep: (step: AudioStep) => void;
  resetAnalyzer: () => void;
};

type RecorderAction =
  | { type: 'SET_STEP'; payload: AudioStep }
  | { type: 'SET_ANALYSER'; payload: AnalyserNode | null }
  | { type: 'SET_IS_RECORDING'; payload: boolean }
  | { type: 'SET_FILE'; payload: File }
  | { type: 'SET_URL'; payload: string };

const RecorderContext = createContext<RecorderContextType | null>(null);

const initialState: RecorderState = {
  step: 'ready',
  analyser: null,
  isRecording: false,
  file: null,
  audioUrl: '',
};

const recorderReducer = (
  state: RecorderState,
  action: RecorderAction
): RecorderState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload };

    case 'SET_ANALYSER':
      return { ...state, analyser: action.payload };

    case 'SET_IS_RECORDING':
      return { ...state, isRecording: action.payload };

    case 'SET_FILE':
      return { ...state, file: action.payload };

    case 'SET_URL':
      return { ...state, audioUrl: action.payload };

    default:
      return state;
  }
};

export const RecorderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(recorderReducer, initialState);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const startRecording = async (
    onComplete?: (file: File) => void | Promise<void>
  ) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunks.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });

      mediaRecorderRef.current = recorder;

      recorder.addEventListener('dataavailable', event => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      });

      recorder.addEventListener('stop', async () => {
        const file = new File(
          [...(audioChunks.current ?? [])],
          `recording-${Date.now()}.webm`,
          {
            type: 'audio/webm',
          }
        );
        const url = URL.createObjectURL(file);

        dispatch({ type: 'SET_FILE', payload: file });
        dispatch({ type: 'SET_URL', payload: url });

        await onComplete?.(file);
      });

      // AudioContext 연결
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      dispatch({ type: 'SET_ANALYSER', payload: analyserRef.current });
      recorder.addEventListener('start', () => {
        dispatch({ type: 'SET_STEP', payload: 'recording' });
      });

      recorder.start();
    } catch (error) {
      console.error('🎤 마이크 권한 오류:', error);
    }
  };

  const stopRecording = (isSubmit?: boolean) => {
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(track => track.stop());

    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current
        ?.close()
        .catch(e => console.warn('AudioContext already closed', e));
    }
    if (isSubmit) {
      dispatch({ type: 'SET_STEP', payload: 'ready' });
    } else {
      dispatch({ type: 'SET_STEP', payload: 'waiting' });
    }
  };

  const pauseRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.pause();
      dispatch({ type: 'SET_STEP', payload: 'pause' });
    }
  };

  const resumeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      dispatch({ type: 'SET_STEP', payload: 'recording' });
    }
  };

  const setIsRecording = (isRecording: boolean) =>
    dispatch({ type: 'SET_IS_RECORDING', payload: isRecording });

  const setStep = (step: AudioStep) => {
    dispatch({ type: 'SET_STEP', payload: step });
  };

  const resetAnalyzer = () => {
    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current
        ?.close()
        .catch(e => console.warn('AudioContext already closed', e));
    }
    analyserRef.current = null;
    sourceRef.current = null;
    audioContextRef.current = null;
    dispatch({ type: 'SET_ANALYSER', payload: null });
  };

  return (
    <RecorderContext.Provider
      value={{
        state,
        dispatch,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        setIsRecording,
        setStep,
        resetAnalyzer,
      }}
    >
      {children}
    </RecorderContext.Provider>
  );
};

export const useRecorder = () => {
  const context = useContext(RecorderContext);
  if (!context) {
    throw new Error('useRecorder must be used within RecorderProvider');
  }
  return context;
};
